import type { HistoryUpdateFormRequest } from './validationSchema';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { Dialog } from '@components/Dialog';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { Button } from '@components/buttons';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { FileUpload, PDFPreviewModal } from '@pages/customer/common/file';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import type { SubscriptionRequestHistoryUpdateDTO } from '@repositories/subscriptionRequestRepository/subscriptionRequest.types';
import type { FileAttachment, PdfFilePreview } from '@customHooks/useFileDialog';
import { useUpdateSubscriptionRequestHistoryMutation } from '@queryHooks/useSubscriptionRequest';
import type { SubscriptionRequestHistoryDTOModel } from '@repositories/subscriptionRequestRepository/models/SubscriptionRequestHistoryDTOModel';
import { useHistoryUpdateForm } from './validationSchema';

type Props = {
  isOpen: boolean;
  toggleModal: (isOpen: boolean) => void;
  clearSelectedHistory: () => void;
  selectedHistory: SubscriptionRequestHistoryDTOModel;
};
export const SubscriptionRequestHistoryUpdateModal = (props: Props) => {
  const { isOpen, toggleModal, clearSelectedHistory, selectedHistory } = props;

  const { t } = useTranslation();
  const { control, watch, handleSubmit, setValue, reset } = useHistoryUpdateForm();
  const {
    uiStore: { toastStore },
  } = useStore();

  const { mutate: updateSubscriptionRequestHistory, isPending } = useUpdateSubscriptionRequestHistoryMutation();

  const comment = watch('comment');
  // 첨부파일 관리

  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [pdfPreview, setPdfPreview] = useState<PdfFilePreview>({
    open: false,
    file: null,
    fileId: null,
    fileName: '',
    numPages: 0,
  });

  const handleChangePdfPreview = (pdfPreview: PdfFilePreview) => {
    setPdfPreview(pdfPreview);
  };

  const validation = {
    success: (data: HistoryUpdateFormRequest) => {
      const rqData: SubscriptionRequestHistoryUpdateDTO = {
        subscriptionRequestId: selectedHistory.subscriptionRequestId,
        subscriptionRequestHistoryId: selectedHistory.id,
        comment: data.comment,
        status: selectedHistory.status,
        files: data.files,
      };
      updateSubscriptionRequestHistory(
        {
          ...rqData,
        },
        {
          onSuccess: () => {
            toastStore.open(t('Purchase_History.Modification_Request_Complete'));
            clearSelectedHistory();
            toggleModal(false);
            reset(
              {
                comment: '',
                files: [],
              },
              {
                keepValues: false,
              },
            );
            setAttachments([]);
          },
          onError: () => {
            toastStore.open(t('Purchase_History.Modification_Request_Failed'));
          },
        },
      );
    },
    error: (errors: any) => {
      console.log('❌ Form validation failed:', errors);
      toastStore.open(t('Purchase_History.Modification_Request_Failed'));
    },
  };

  const handleChangeAttachments = (attachments: FileAttachment[]) => {
    setAttachments(attachments);
    setValue(
      'files',
      attachments.map(attachment => attachment.file),
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => toggleModal(false)}
      title={t('Purchase_History.Modification_Request')}
      size="medium"
    >
      <ErrorBoundary key={isOpen ? 'open' : 'closed'}>
        <Form onSubmit={handleSubmit(validation.success, validation.error)}>
          <Element>
            <InfoWrap>
              <InfoItem>
                <InfoText>{t('Purchase_History.Processing_Date')}</InfoText>
                <InfoValue>{dayjs(selectedHistory.createdDate).format('YYYY.MM.DD')}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoText>{t('Purchase_History.Processing_Manager')}</InfoText>
                <InfoValue>{selectedHistory.managerName ?? '-'}</InfoValue>
              </InfoItem>
            </InfoWrap>
            <InfoWrap>
              <InfoItem>
                <InfoText>{t('Purchase_History.Processing_Content')}</InfoText>
                <InfoValue>{selectedHistory.comment ?? '-'}</InfoValue>
              </InfoItem>
            </InfoWrap>
          </Element>
          <Hr />
          <Element>
            <TextAreaWrap>
              <TextAreaInfo>
                <MainInfoText>{t('Purchase_History.Processing_Content')}</MainInfoText>
                <MaxLength>{`(${comment?.length ?? 0}/200)`}</MaxLength>
              </TextAreaInfo>
              <Controller
                control={control}
                name="comment"
                render={({ field: { onChange, value } }) => (
                  <TextArea
                    maxLength={200}
                    placeholder={t('Purchase_History.Processing_Content_Placeholder')}
                    value={value ?? ''}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                  />
                )}
              />
            </TextAreaWrap>
          </Element>
          <Element>
            <FileUploadWrap>
              <MainInfoText>{t('Purchase_History.Processing_Attachment')}</MainInfoText>
              <FileUpload
                attachments={attachments ?? []}
                updateAttachments={handleChangeAttachments}
                updatePdfPreview={handleChangePdfPreview}
              />
            </FileUploadWrap>
          </Element>
          <Footer>
            <SecureSubmitButton variant="contain" size="extraLarge" type="submit" isPending={isPending}>
              {t('Purchase_History.Processing_Send')}
            </SecureSubmitButton>
            <Button variant="outline" size="extraLarge" type="button" onClick={() => toggleModal(false)}>
              {t('Purchase_History.Processing_Cancel')}
            </Button>
          </Footer>
        </Form>
        <PDFPreviewModal pdfPreview={pdfPreview} updatePdfPreview={setPdfPreview} />
      </ErrorBoundary>
    </Dialog>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 17px 0;
  padding-bottom: 94px;
`;

const Element = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoWrap = styled(Box)`
  display: flex;
  gap: 8px;
`;

const InfoItem = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const InfoValue = styled.p`
  ${fonts.Headline8};
  color: ${({ theme }) => theme.colors['text-gray-main']};
`;

const InfoText = styled.strong`
  ${fonts.Headline9};
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
`;

const Hr = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.colors['border-gray-w-lighter']};
`;

const TextAreaWrap = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TextAreaInfo = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MainInfoText = styled.strong`
  ${fonts.Headline8};
  color: ${({ theme }) => theme.colors['text-gray-main']};
`;

const MaxLength = styled.span`
  ${fonts.Body4};
  color: ${({ theme }) => theme.colors['text-gray-light']};
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 120px;
  padding: 5px 11px;
  resize: none;
  background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
  border-radius: 2px;
  ${fonts.Body2};
`;

const FileUploadWrap = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Footer = styled(Dialog.Footer)`
  display: flex;
  padding: 0 40px;
`;
